import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, InputBase, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { glassSx } from '../theme/glass';
import posthog from '../utils/posthog';

interface SearchBarProps {
  isMobile?: boolean;
  setSearchOpen?: (open: boolean) => void;
  initialValue?: string;
  autoFocus?: boolean;
}

const noopSetSearchOpen: (open: boolean) => void = () => {
  return undefined;
};

const SearchBar: FC<SearchBarProps> = ({
  isMobile = false,
  setSearchOpen = noopSetSearchOpen,
  initialValue = '',
  autoFocus = false,
}) => {
  const [searchInput, setSearchInput] = useState(initialValue);
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchInput === '') return;
    posthog.capture('search_executed', { query: searchInput });
    navigate(`/search/${searchInput}`);
  };

  const clearSearch = () => {
    if (isMobile) {
      setSearchOpen(false);
    }
    setSearchInput('');
  };

  const theme = useTheme();

  // Guide `.searchbar`: glass pill with a leading muted icon and borderless input.
  return (
    <Box sx={{ flexGrow: 0.75, '@media (max-width:960px)': { flexGrow: 1, px: 'var(--space-stack-tight)' } }}>
      <Box
        component="form"
        onSubmit={handleSearch}
        sx={{
          ...glassSx(theme.tokens, 1),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderRadius: 'var(--sh-full)',
          px: 2.5,
          py: 1.125,
        }}
      >
        <SearchIcon sx={{ fontSize: 18, color: theme.tokens.colors.text3, flex: 'none' }} />
        <InputBase
          type="search"
          placeholder="Search posts, herds, users…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          fullWidth
          autoFocus={autoFocus}
          sx={{ fontSize: '14.5px', color: theme.tokens.colors.textPrimary }}
        />
        {(searchInput || isMobile) && (
          <IconButton color="primary" size="small" onClick={clearSearch} sx={{ flex: 'none' }}>
            <HighlightOffIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SearchBar;
